import React, { useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text, RoundedBox } from '@react-three/drei';
import * as THREE from 'three';

interface Book3DProps {
  title?: string;
  author?: string;
  color?: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  onClick?: () => void;
  hoverable?: boolean;
}

const Book3D: React.FC<Book3DProps> = ({
  title = 'Through the Fence',
  author = 'Ano Mkhulisi',
  color = '#c9a84c',
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  onClick,
  hoverable = true,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const [hovered, setHovered] = useState(false);

  useFrame((state) => {
    if (groupRef.current) {
      if (hoverable && hovered) {
        groupRef.current.rotation.y = THREE.MathUtils.lerp(
          groupRef.current.rotation.y,
          rotation[1] + 0.3,
          0.05
        );
        groupRef.current.position.y = THREE.MathUtils.lerp(
          groupRef.current.position.y,
          position[1] + 0.15,
          0.05
        );
      } else {
        groupRef.current.rotation.y = THREE.MathUtils.lerp(
          groupRef.current.rotation.y,
          rotation[1] + Math.sin(state.clock.elapsedTime * 0.5) * 0.05,
          0.05
        );
        groupRef.current.position.y = THREE.MathUtils.lerp(
          groupRef.current.position.y,
          position[1] + Math.sin(state.clock.elapsedTime * 0.8) * 0.05,
          0.05
        );
      }
    }
  });

  return (
    <group
      ref={groupRef}
      position={position}
      rotation={rotation as any}
      scale={scale}
      onClick={onClick}
      onPointerOver={() => {
        if (hoverable) {
          setHovered(true);
          document.body.style.cursor = 'pointer';
        }
      }}
      onPointerOut={() => {
        setHovered(false);
        document.body.style.cursor = 'default';
      }}
    >
      {/* Book cover - front */}
      <RoundedBox args={[2, 2.8, 0.3]} radius={0.02} position={[0, 0, 0]}>
        <meshStandardMaterial
          color={hovered ? '#e8d48b' : color}
          metalness={0.3}
          roughness={0.4}
        />
      </RoundedBox>

      {/* Book spine */}
      <mesh position={[-1.0, 0, 0]}>
        <boxGeometry args={[0.05, 2.8, 0.3]} />
        <meshStandardMaterial
          color={new THREE.Color(color).multiplyScalar(0.7)}
          metalness={0.4}
          roughness={0.3}
        />
      </mesh>

      {/* Pages effect */}
      <mesh position={[0, 0, 0]}>
        <boxGeometry args={[1.9, 2.7, 0.25]} />
        <meshStandardMaterial color="#f5f0e8" roughness={0.8} />
      </mesh>

      {/* Title text */}
      <Text
        position={[0, 0.4, 0.16]}
        fontSize={0.2}
        color="#0a0a0f"
        font="https://fonts.gstatic.com/s/playfairdisplay/v30/nuFvD-vYSZviVYUb_rj3ij__anPXJzDwcbmjWBN2PKdFvXDTbtPY_Q.woff2"
        maxWidth={1.6}
        textAlign="center"
        anchorX="center"
        anchorY="middle"
        lineHeight={1.2}
      >
        {title}
      </Text>

      {/* Author text */}
      <Text
        position={[0, -0.6, 0.16]}
        fontSize={0.12}
        color="#333"
        maxWidth={1.6}
        textAlign="center"
        anchorX="center"
        anchorY="middle"
      >
        {author}
      </Text>

      {/* Decorative line */}
      <mesh position={[0, 0, 0.155]}>
        <planeGeometry args={[1.2, 0.01]} />
        <meshStandardMaterial color="#0a0a0f" />
      </mesh>

      {/* Infinity symbol on cover */}
      <Text
        position={[0, -1.0, 0.16]}
        fontSize={0.25}
        color={new THREE.Color(color).multiplyScalar(0.6)}
        anchorX="center"
        anchorY="middle"
      >
        &#8734;
      </Text>

      {/* Glow effect when hovered */}
      {hovered && (
        <pointLight
          position={[0, 0, 1]}
          color={color}
          intensity={1}
          distance={3}
        />
      )}
    </group>
  );
};

export default Book3D;
